import React, { useEffect, useRef, useState } from "react";
import ModalComponent from "../ModalComponent/ModalComponent";
import BankUserLookup from "./BankUserLookup";
import BankDetailForm from "./BankDetailForm";
import UserBankDetailsList from "./UserBankDetailsList";
import { useFetchAppUserByMobileMutation } from "../../../features/tenant/appUsers/appUsersApi";
import {
  useAddBankDetailMutation,
  useDeleteBankDetailMutation,
  useGetBankDetailByAppUserIdMutation,
  useUpdateBankDetailStatusMutation,
} from "../../../features/tenant/appUserBankDetails/appUserBankApi";
import toast from "react-hot-toast";

const BankDetailManageModal = ({ show, setShow, onSuccess }) => {
  const [mobile, setMobile] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [bankDetails, setBankDetails] = useState([]);
  const [formKey, setFormKey] = useState(0);
  const lastSearchedMobileRef = useRef("");

  const token = sessionStorage.getItem("access_token");
  const tenant_id = JSON.parse(sessionStorage.getItem("vendorData")).tenant_id;

  const [fetchAppUserByMobile, { data: userData, isLoading: userLoading, reset: resetUserSearch }] =
    useFetchAppUserByMobileMutation();

  const [fetchBankDetails, { isLoading: bankDetailsLoading }] =
    useGetBankDetailByAppUserIdMutation();

  const [addBankDetail, { isLoading: addLoading }] = useAddBankDetailMutation();
  const [updateBankStatus, { isLoading: updateLoading }] =
    useUpdateBankDetailStatusMutation();
  const [deleteBankDetail, { isLoading: deleteLoading }] =
    useDeleteBankDetailMutation();

  const loadBankDetails = async (appUserId) => {
    try {
      const response = await fetchBankDetails({
        token,
        tenant_id,
        id: appUserId,
        includeDeleted: true,
      }).unwrap();

      setBankDetails(Array.isArray(response?.body) ? response.body : []);
    } catch {
      setBankDetails([]);
    }
  };

  useEffect(() => {
    if (mobile.length !== 10) {
      lastSearchedMobileRef.current = "";
      setUserInfo(null);
      setBankDetails([]);
      return;
    }

    if (lastSearchedMobileRef.current === mobile) {
      return;
    }

    lastSearchedMobileRef.current = mobile;
    setUserInfo(null);
    setBankDetails([]);
    resetUserSearch();

    fetchAppUserByMobile({
      token,
      tenant_id,
      mobile,
    });
  }, [mobile]);

  useEffect(() => {
    if (!userData || mobile.length !== 10) return;
    if (lastSearchedMobileRef.current !== mobile) return;

    const user = userData?.body;

    if (user?.id) {
      setUserInfo({ ...user, mobile });
      loadBankDetails(user.id);
      return;
    }

    setUserInfo(null);
    setBankDetails([]);
    toast.error("User not found");
  }, [userData]);

  const handleClose = () => {
    setMobile("");
    setUserInfo(null);
    setBankDetails([]);
    lastSearchedMobileRef.current = "";
    resetUserSearch();
    setShow(false);
  };

  const handleAddBankDetail = async (formData) => {
    if (!userInfo?.id) {
      toast.error("Please enter a valid mobile number first");
      return;
    }

    try {
      await addBankDetail({
        token,
        tenant_id,
        body: {
          ...formData,
          app_user_id: userInfo.id,
        },
      }).unwrap();

      toast.success("Bank details added successfully");
      setFormKey((prev) => prev + 1);
      await loadBankDetails(userInfo.id);
      onSuccess?.();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to add bank details");
    }
  };

  const handleSetActive = async (item) => {
    if (!userInfo?.id) return;

    try {
      await updateBankStatus({
        token,
        tenant_id,
        id: item.id,
        body: { app_user_id: userInfo.id },
      }).unwrap();

      toast.success("Set as active bank detail");
      await loadBankDetails(userInfo.id);
      onSuccess?.();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to set active bank detail");
    }
  };

  const handleRestore = async (item) => {
    if (!userInfo?.id) return;

    try {
      await updateBankStatus({
        token,
        tenant_id,
        id: item.id,
        body: { app_user_id: userInfo.id, restore: true },
      }).unwrap();

      toast.success("Bank detail restored");
      await loadBankDetails(userInfo.id);
      onSuccess?.();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to restore bank detail");
    }
  };

  const handleDelete = async (item) => {
    if (
      !window.confirm(
        "Soft delete this bank detail? It can be restored later."
      )
    ) {
      return;
    }

    try {
      await deleteBankDetail({
        token,
        tenant_id,
        id: item.id,
      }).unwrap();

      toast.success("Bank detail deleted");
      await loadBankDetails(userInfo.id);
      onSuccess?.();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to delete bank detail");
    }
  };

  return (
    <ModalComponent size="2xl" show={show} setShow={handleClose}>
      <div className="w-full p-4">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Manage Bank Details
        </h2>

        <BankUserLookup
          mobile={mobile}
          setMobile={setMobile}
          userInfo={userInfo}
          isLoading={userLoading}
        />

        {userInfo?.id && (
          <>
            <div className="mb-4">
              <h3 className="text-base font-semibold text-gray-800 mb-2">
                Existing Bank Details
              </h3>
              {bankDetailsLoading ? (
                <p className="text-sm text-gray-500">Loading bank details...</p>
              ) : (
                <UserBankDetailsList
                  bankDetails={bankDetails}
                  onSetActive={handleSetActive}
                  onRestore={handleRestore}
                  onDelete={handleDelete}
                  isUpdating={updateLoading}
                  isRestoring={updateLoading}
                  isDeleting={deleteLoading}
                />
              )}
            </div>

            <BankDetailForm
              key={formKey}
              onSubmit={handleAddBankDetail}
              isSubmitting={addLoading}
            />
          </>
        )}
      </div>
    </ModalComponent>
  );
};

export default BankDetailManageModal;
